import { motion } from 'framer-motion';
import { AlertTriangle, Home, RefreshCw } from 'lucide-react';
import { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: { componentStack: string } | null;
}

class ErrorBoundaryClass extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: { componentStack: string }) {
    this.setState({
      error,
      errorInfo,
    });

    if (import.meta.env.DEV) {
      console.error('Error caught by boundary:', error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleGoHome = () => {
    this.handleReset();
    window.location.href = '/dashboard';
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorFallback
          error={this.state.error}
          onReset={this.handleReset}
          onGoHome={this.handleGoHome}
          onReload={this.handleReload}
        />
      );
    }

    return this.props.children;
  }
}

interface ErrorFallbackProps {
  error: Error | null;
  onReset: () => void;
  onGoHome: () => void;
  onReload: () => void;
}

function ErrorFallback({ error, onReset, onGoHome, onReload }: ErrorFallbackProps) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-neolab-deep-green dark:to-neolab-forest p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl w-full bg-white dark:bg-neolab-forest rounded-3xl shadow-2xl border border-neolab-green/10 dark:border-white/10 p-8 md:p-12"
      >
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring' }}
            className="w-20 h-20 mx-auto mb-6 rounded-full bg-rose-500/10 flex items-center justify-center"
          >
            <AlertTriangle className="w-10 h-10 text-rose-500" />
          </motion.div>

          <h1 className="text-3xl font-black text-slate-900 dark:text-white mb-3 tracking-tight">
            Oops! Something went wrong
          </h1>
          <p className="text-slate-500 dark:text-slate-400 font-medium">
            We encountered an unexpected error. Don't worry, your data is safe.
          </p>
        </div>

        {error && import.meta.env.DEV && (
          <div className="mb-8 p-4 rounded-2xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
            <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2">
              Error Details (Development Only)
            </p>
            <p className="text-sm font-mono text-rose-600 dark:text-rose-400 break-all">
              {error.message}
            </p>
            {error.stack && (
              <details className="mt-3">
                <summary className="text-xs font-bold text-slate-500 cursor-pointer hover:text-slate-700 dark:hover:text-slate-300">
                  Stack Trace
                </summary>
                <pre className="mt-2 text-xs font-mono text-slate-600 dark:text-slate-400 overflow-auto max-h-40 p-2 bg-slate-50 dark:bg-slate-900 rounded-lg">
                  {error.stack}
                </pre>
              </details>
            )}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-4">
          <button
            type="button"
            onClick={onGoHome}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-4 rounded-2xl bg-neolab-green text-white font-black text-sm shadow-lg shadow-green-500/20 hover:bg-neolab-green/90 transition-all"
          >
            <Home className="w-5 h-5" />
            Go to Dashboard
          </button>
          <button
            type="button"
            onClick={onReset}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-4 rounded-2xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-black text-sm hover:bg-slate-200 dark:hover:bg-slate-700 transition-all"
          >
            <RefreshCw className="w-5 h-5" />
            Try Again
          </button>
          <button
            type="button"
            onClick={onReload}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-4 rounded-2xl bg-blue-500 text-white font-black text-sm shadow-lg shadow-blue-500/20 hover:bg-blue-600 transition-all"
          >
            <RefreshCw className="w-5 h-5" />
            Reload Page
          </button>
        </div>

        <div className="mt-8 pt-8 border-t border-slate-200 dark:border-slate-700 text-center">
          <p className="text-xs text-slate-400 dark:text-slate-500 font-medium">
            If this problem persists, please contact your administrator or try refreshing the page.
          </p>
        </div>
      </motion.div>
    </div>
  );
}

export function ErrorBoundary({ children, fallback }: Props) {
  return <ErrorBoundaryClass fallback={fallback}>{children}</ErrorBoundaryClass>;
}
